import React from 'react';
// import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {BrowserRouter} from "react-router-dom";
import './global.css';
import {Provider} from 'react-redux';
import {store, persistor} from './store';
import {PersistGate} from "redux-persist/integration/react";
import {QueryClient, QueryClientProvider} from 'react-query';
import {createRoot} from 'react-dom/client';
import {tokenStorage} from './app/tokenStorage';
import {GoogleOAuthProvider} from '@react-oauth/google';
import {GOOGLE_CLIENT_ID} from './app/config';

// CRITICAL: Force CSS reload on hard refresh (Ctrl+F5) - Backup to HTML script
// This runs as a fallback if the HTML script didn't catch all CSS
(function forceCSSReload() {
    // Only run if HTML script didn't already handle it (check for _v param)
    const checkAndReloadCSS = () => {
        const cssLinks = document.querySelectorAll('link[rel="stylesheet"]:not([data-cache-busted])');
        let needsReload = false;
        
        cssLinks.forEach((link) => {
            const href = link.getAttribute('href') || '';
            if (href && (href.includes('.css') || href.includes('static/css'))) {
                // Check if already has cache-busting from HTML script
                if (href.indexOf('_v=') === -1) {
                    needsReload = true;
                    
                    // Add cache-busting query parameter
                    try {
                        const url = new URL(href, window.location.origin);
                        url.searchParams.set('_v', Date.now());
                        
                        // Update href directly (simpler than replacing element)
                        link.setAttribute('href', url.toString());
                        link.setAttribute('data-cache-busted', 'true');
                    } catch (e) {
                        // Fallback for relative URLs
                        const separator = href.includes('?') ? '&' : '?';
                        link.setAttribute('href', href + separator + '_v=' + Date.now());
                        link.setAttribute('data-cache-busted', 'true');
                    }
                } else {
                    // Already has cache-busting, mark it
                    link.setAttribute('data-cache-busted', 'true');
                }
            }
        });
        
        return needsReload;
    };
    
    // Run after a short delay to let HTML script run first
    setTimeout(() => {
        if (checkAndReloadCSS()) {
            // If we had to add cache-busting, also check again after React loads
            setTimeout(checkAndReloadCSS, 500);
        }
    }, 150);
})();

// Preload session from cookies on page load
tokenStorage.preloadSessionFromCookies();

// Aggressive cache-busting: Unregister any service workers that might cache old files
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
            registration.unregister().then(() => {
            });
        }
    });
    
    // Clear all caches
    if ('caches' in window) {
        caches.keys().then((cacheNames) => {
            return Promise.all(
                cacheNames.map((cacheName) => {
                    return caches.delete(cacheName);
                })
            );
        }).then(() => {
        });
    }
}

// Aggressive cache-busting on page load for Ctrl+F5 issues
(function() {
    // Clear any old cache flags
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('_nocache')) {
        urlParams.delete('_nocache');
        const cleanUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', cleanUrl);
    }
})();

// Handle ChunkLoadError and CSS loading issues - reload page if chunks fail to load (stale cache issue)
let isReloading = false;
const handleChunkError = () => {
    if (isReloading) return;
    
    // Check if we've already tried to reload (to prevent infinite loops)
    const reloadAttempt = sessionStorage.getItem('chunkErrorReload');
    if (reloadAttempt) {
        const lastAttempt = parseInt(reloadAttempt, 10);
        if (Date.now() - lastAttempt < 3000) {
            // Already tried to reload recently, don't try again
            console.warn('Already attempted reload recently, skipping...');
            return;
        }
    }
    
    isReloading = true;
    sessionStorage.setItem('chunkErrorReload', Date.now().toString());
    
    console.warn('Detected chunk/CSS loading issue, reloading with cache bypass...');
    
    // Force reload with cache bypass using timestamp
    const url = new URL(window.location.href);
    url.searchParams.set('_nocache', Date.now());
    window.location.replace(url.toString());
};

// Enhanced CSS verification with better detection
const verifyCSSLoaded = () => {
    // Wait a bit for CSS to load
    setTimeout(() => {
        try {
            // Check all CSS links
            const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
            let allCSSLoaded = true;
            let hasMainCSS = false;
            
            cssLinks.forEach((link) => {
                const href = link.getAttribute('href') || '';
                if (href.includes('.css') || href.includes('static/css')) {
                    hasMainCSS = true;
                    
                    // Check if stylesheet is actually loaded
                    let cssLoaded = false;
                    try {
                        const sheet = link.sheet;
                        if (sheet && (sheet.cssRules || sheet.rules)) {
                            const ruleCount = (sheet.cssRules?.length || sheet.rules?.length || 0);
                            if (ruleCount > 0) {
                                cssLoaded = true;
                            }
                        }
                    } catch (e) {
                        // Cross-origin error - check if link is in DOM and has href
                        if (link.href && document.contains(link)) {
                            cssLoaded = true; // Assume loaded if link exists
                        }
                    }
                    
                    if (!cssLoaded) {
                        allCSSLoaded = false;
                    }
                }
            });
            
            // Also verify body has computed styles (indicates CSS is loaded)
            if (hasMainCSS) {
                const bodyStyles = window.getComputedStyle(document.body);
                const hasStyles = bodyStyles.fontFamily && 
                                 bodyStyles.fontFamily !== 'initial' && 
                                 bodyStyles.fontFamily !== '' &&
                                 bodyStyles.margin !== 'initial';
                
                if (!hasStyles && allCSSLoaded) {
                    allCSSLoaded = false;
                }
            }
            
            // If CSS not loaded properly, force reload
            if (hasMainCSS && !allCSSLoaded) {
                console.warn('CSS not fully loaded, attempting to reload CSS links...');
                
                // Try to reload CSS links one more time
                cssLinks.forEach((link) => {
                    const href = link.getAttribute('href');
                    if (href && (href.includes('.css') || href.includes('static/css'))) {
                        const url = new URL(href, window.location.origin);
                        url.searchParams.set('_v', Date.now());
                        link.href = url.toString();
                    }
                });
                
                // Check again after reload
                setTimeout(() => {
                    const bodyStyles2 = window.getComputedStyle(document.body);
                    const stillNoStyles = !bodyStyles2.fontFamily || 
                                        bodyStyles2.fontFamily === 'initial' || 
                                        bodyStyles2.fontFamily === '';
                    if (stillNoStyles) {
                        console.warn('CSS still not loaded properly, reloading page...');
                        handleChunkError();
                    }
                }, 1500);
            }
        } catch (e) {
            console.error('Error verifying CSS:', e);
        }
    }, 500);
};

// Run verification when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', verifyCSSLoaded);
} else {
    verifyCSSLoaded();
}

// Listen for chunk loading errors
window.addEventListener('error', (event) => {
    const errorMsg = String(event.message || '');
    const target = event.target;
    
    // Silently ignore manifest.json errors (not critical for app functionality)
    if (target && target.href && target.href.includes('manifest.json')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
    
    // Also ignore manifest.json fetch errors from service workers
    if (errorMsg.includes('manifest.json') || errorMsg.includes('Manifest fetch')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
    
    // Handle CSS loading errors
    if (target && target.tagName === 'LINK' && target.rel === 'stylesheet') {
        const href = target.href || '';
        if (href.includes('.chunk.css') || href.includes('static/css/') || (href.includes('main.') && href.includes('.css'))) {
            console.warn('CSS chunk failed to load:', href);
            handleChunkError();
            return;
        }
    }
    
    // Handle JS chunk loading errors
    if (errorMsg.includes('Loading chunk') || errorMsg.includes('ChunkLoadError') || errorMsg.includes('Failed to fetch dynamically imported module')) {
        event.preventDefault();
        console.warn('ChunkLoadError detected:', errorMsg);
        handleChunkError();
    }
}, true);

window.addEventListener('unhandledrejection', (event) => {
    const reason = String(event.reason?.message || event.reason || '');
    
    // Ignore manifest.json related rejections
    if (reason.includes('manifest.json') || reason.includes('Manifest fetch')) {
        event.preventDefault();
        return;
    }
    
    if (reason.includes('Loading chunk') || reason.includes('ChunkLoadError') || reason.includes('Failed to fetch dynamically imported module')) {
        event.preventDefault();
        console.warn('ChunkLoadError in promise rejection:', reason);
        handleChunkError();
    }
});

const container = document.getElementById('root');
const root = createRoot(container);
const queryClient = new QueryClient();

root.render(
    <React.StrictMode>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ''}>
            <Provider store={store}>
                <PersistGate persistor={persistor}>
                    <QueryClientProvider client={queryClient}>
                         <BrowserRouter>
                              <App />
                         </BrowserRouter>
                    </QueryClientProvider>
                </PersistGate>
            </Provider>
        </GoogleOAuthProvider>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
